import { Badge, Button, Checkbox, Stack } from '@qcx/ui';
import {
  CancelInvoiceButton,
  CreateExternalPaymentButton,
  CreatePaymentButton
} from './InvoiceActionButtons';
import { CopyToClipboard } from 'components/CopyToClipboard';
import { INVOICE_STATUS_MAP, handleInvoiceFee } from './utils';
import { IOrderPayment, IOrderPaymentPlansDetail } from 'interfaces/orders';
import { Table } from 'antd';
import { TextField } from '@refinedev/antd';
import { formatDate } from 'services/date';
import { formatMoney } from 'services/money';
import { paymentPlanInvoicesStatus } from 'services/payment-plan';

const renderStatusTag = (status: string) => {
  const translated = paymentPlanInvoicesStatus(status);
  const variant = INVOICE_STATUS_MAP[status] || 'primary';

  return <Badge variant={variant}>{translated}</Badge>;
};

const renderExternalPaymentStatus = (externalPayment?: IOrderPayment) => {
  if (!externalPayment) {
    return null;
  }

  if (externalPayment.status !== 'approved') {
    return <>Baixa em processamento</>;
  }

  return <>Baixa realizada</>;
};

const BoletoActions = ({
  currentPayment,
  invoice,
  plan,
  orderId
}: {
  currentPayment: IOrderPayment;
  invoice: IOrderPaymentPlansDetail['invoices'][number];
  plan: IOrderPaymentPlansDetail;
  orderId: string;
}) => {
  return (
    <Stack flow="column" css={{ alignItems: 'flex-start', gap: '$5' }}>
      {invoice.status !== 'cancelled' && (
        <Button
          variant="neutralLink"
          size="sm"
          as="a"
          target="blank"
          href={(currentPayment?.info?.url as string) || '404'}
        >
          Visualizar boleto
        </Button>
      )}
      {plan.status === 'cancelled' && invoice.status !== 'cancelled' && (
        <CancelInvoiceButton invoice={invoice} orderId={orderId} />
      )}
    </Stack>
  );
};

const PixActions = ({
  currentPayment,
  invoice,
  plan,
  orderId
}: {
  currentPayment: IOrderPayment;
  invoice: IOrderPaymentPlansDetail['invoices'][number];
  plan: IOrderPaymentPlansDetail;
  orderId: string;
}) => {
  return (
    <Stack flow="column" css={{ alignItems: 'flex-start', gap: '$5' }}>
      {invoice.status !== 'cancelled' && (
        <CopyToClipboard
          size="sm"
          text={currentPayment?.info?.qr_code || 'Código não encontrado'}
        >
          Copiar código
        </CopyToClipboard>
      )}
      {plan.status === 'cancelled' && invoice.status !== 'cancelled' && (
        <CancelInvoiceButton invoice={invoice} orderId={orderId} />
      )}
    </Stack>
  );
};

const PendingInvoiceActions = ({
  invoice,
  orderId,
  preferences,
  plan
}: {
  invoice: IOrderPaymentPlansDetail['invoices'][number];
  orderId: string;
  preferences: Record<string, unknown>;
  plan: IOrderPaymentPlansDetail;
}) => {
  return (
    <Stack flow="column" css={{ alignItems: 'flex-start', gap: '$5' }}>
      <CreatePaymentButton
        invoice={invoice}
        orderId={orderId}
        preferences={preferences}
      />
      <CreateExternalPaymentButton
        invoice={invoice}
        orderId={orderId}
        preferences={preferences}
      />
      {plan.status === 'cancelled' && (
        <CancelInvoiceButton invoice={invoice} orderId={orderId} />
      )}
    </Stack>
  );
};

const InitialInvoiceActions = ({
  invoice,
  orderId,
  preferences
}: {
  invoice: IOrderPaymentPlansDetail['invoices'][number];
  orderId: string;
  preferences: Record<string, unknown>;
}) => {
  return (
    <Stack flow="column" css={{ alignItems: 'flex-start', gap: '$5' }}>
      <CreatePaymentButton
        invoice={invoice}
        orderId={orderId}
        preferences={preferences}
      />
      <CreateExternalPaymentButton
        invoice={invoice}
        orderId={orderId}
        preferences={preferences}
      />
    </Stack>
  );
};

const InvoicePaymentCell = ({
  payments,
  invoice,
  orderId,
  preferences,
  plan
}: {
  payments: IOrderPayment[];
  invoice: IOrderPaymentPlansDetail['invoices'][number];
  orderId: string;
  preferences: Record<string, unknown>;
  plan: IOrderPaymentPlansDetail;
}) => {
  const externalPayment = payments.find(p => p.kind === 'external');
  const externalPaymentStatus = renderExternalPaymentStatus(externalPayment);

  if (externalPaymentStatus) {
    return externalPaymentStatus;
  }

  const currentPayment = payments.find(
    payment =>
      payment['expires_at'] && new Date(payment['expires_at']) > new Date()
  );

  if (currentPayment?.kind === 'boleto') {
    return (
      <BoletoActions
        currentPayment={currentPayment}
        invoice={invoice}
        plan={plan}
        orderId={orderId}
      />
    );
  }

  if (currentPayment?.kind === 'pix') {
    return (
      <PixActions
        currentPayment={currentPayment}
        invoice={invoice}
        plan={plan}
        orderId={orderId}
      />
    );
  }

  if (invoice.status === 'due') {
    return (
      <PendingInvoiceActions
        invoice={invoice}
        orderId={orderId}
        preferences={preferences}
        plan={plan}
      />
    );
  }

  if (invoice.status === 'initial') {
    return (
      <InitialInvoiceActions
        invoice={invoice}
        orderId={orderId}
        preferences={preferences}
      />
    );
  }

  return <></>;
};

export const InvoiceTable = ({
  data,
  onSelect,
  showPayments,
  orderId,
  preferences,
  plan
}: {
  data: IOrderPaymentPlansDetail['invoices'];
  onSelect?: (
    value: boolean,
    currentInv: IOrderPaymentPlansDetail['invoices'][number]
  ) => void;
  showPayments?: boolean;
  orderId: string;
  preferences: Record<string, unknown>;
  plan: IOrderPaymentPlansDetail;
}) => {
  return (
    <Table
      dataSource={data as IOrderPaymentPlansDetail['invoices']}
      rowKey="id"
      pagination={false}
      bordered
      scroll={{ x: true }}
      style={{ width: '100%', maxWidth: 'unset' }}
    >
      <Table.Column
        dataIndex="index"
        key="index"
        title="Parcela"
        render={(amount, currentInvoice) => {
          const onChange = (v: boolean) => {
            onSelect?.(
              v,
              currentInvoice as IOrderPaymentPlansDetail['invoices'][number]
            );
          };

          return (
            <Stack css={{ gap: '$2' }}>
              {onSelect ? (
                <Checkbox label={`${amount + 1}`} onCheckedChange={onChange} />
              ) : (
                <TextField value={amount + 1} />
              )}
            </Stack>
          );
        }}
      />
      <Table.Column
        dataIndex="total_amount"
        key="total_amount"
        title="Valor total"
        render={amount => <TextField value={formatMoney(amount)} />}
      />
      <Table.Column<IOrderPaymentPlansDetail['invoices'][number]>
        dataIndex="fee"
        key="fee"
        title="Juros e Multa"
        render={(_, record) => <TextField value={handleInvoiceFee(record)} />}
      />
      <Table.Column
        dataIndex="due_at"
        key="due_at"
        title="Vencimento"
        render={amount => (
          <TextField value={formatDate(amount, 'DD/MM/YYYY')} />
        )}
      />
      <Table.Column
        dataIndex="status"
        key="status"
        title="Status"
        render={status => renderStatusTag(status)}
      />
      {showPayments && (
        <Table.Column
          dataIndex="payments"
          key="payments"
          title="Pagamento"
          render={(
            payments: IOrderPayment[],
            invoice: IOrderPaymentPlansDetail['invoices'][number]
          ) => (
            <InvoicePaymentCell
              payments={payments}
              invoice={invoice}
              orderId={orderId}
              preferences={preferences}
              plan={plan}
            />
          )}
        />
      )}
    </Table>
  );
};
